import * as React from "react";
import { PermissionWrapper, Button } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

export function PermissionWrapperDemo() {
  // Drive a real permission flip so the dimmed -> live opacity transition is
  // visible on a live state change (a role change / workspace switch in the app).
  const [allowed, setAllowed] = React.useState(false);

  return (
    <Section
      id="permission-wrapper"
      title="PermissionWrapper"
      description="Gate any action and, when it is off, tell the user why. Wraps a single control (usually a Button): when permission is denied it disables the child, swallows its click, dims it, and shows the reason in a Tooltip. Built on the in-house Tooltip primitive, so focus management, portalling, and aria wiring come for free. UX only, never a security boundary."
    >
      <VariantBlock label="Allowed" caption="child renders untouched and stays clickable">
        <PermissionWrapper hasPermission>
          <Button onClick={() => {}}>Edit</Button>
        </PermissionWrapper>
      </VariantBlock>

      <VariantBlock
        label="Allowed with tooltip"
        caption="hover the live control for an informational hint"
      >
        <PermissionWrapper
          hasPermission
          allowedTooltip="Upload documents or connect sources to your Vault."
        >
          <Button>Upload</Button>
        </PermissionWrapper>
      </VariantBlock>

      <VariantBlock
        label="Denied"
        caption="disabled child; hover for the reason"
      >
        <PermissionWrapper
          hasPermission={false}
          deniedMessage="Only team owners and admins can remove Co-Creators."
        >
          <Button variant="destructive">Remove</Button>
        </PermissionWrapper>
      </VariantBlock>

      <VariantBlock label="Tones and hideOnDenied">
        <PermissionWrapper
          hasPermission={false}
          tone="subtle"
          deniedMessage="Viewers cannot add content to the Vault."
        >
          <Button variant="outline">Add content (subtle)</Button>
        </PermissionWrapper>
        <PermissionWrapper hasPermission={false} hideOnDenied>
          <Button>Hidden when denied</Button>
        </PermissionWrapper>
        <span className="self-center text-xs text-muted-foreground">
          hideOnDenied renders nothing, so no button appears beside the subtle one.
        </span>
      </VariantBlock>

      <VariantBlock
        label="Interactive"
        caption="toggle the role to ease the control from dimmed to live"
      >
        <div className="flex flex-wrap items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => setAllowed((v) => !v)}>
            {allowed ? "Revoke permission" : "Grant permission"}
          </Button>
          <PermissionWrapper
            hasPermission={allowed}
            deniedMessage="You do not have permission in this workspace for this action."
          >
            <Button onClick={() => {}}>Publish</Button>
          </PermissionWrapper>
        </div>
      </VariantBlock>
    </Section>
  );
}
